import { CalendarEvent } from "./types";
import { BerthResponse as BerthDetails } from "@/types/berth-management.types";
import { IAddonData } from "@/types/addon-management.types";
import { SeasonDateRange } from "@/types/harbor-management.types";

const extractAddonData = (
  addonItem: { data?: IAddonData } | IAddonData,
): IAddonData => {
  return "data" in addonItem && addonItem.data
    ? addonItem.data
    : (addonItem as IAddonData);
};

export const ISO_DATE_PREFIX_LENGTH = 10;

export const formatDateDay = (d: Date) =>
  d.toISOString().substring(0, ISO_DATE_PREFIX_LENGTH);

/** Shift a YYYY-MM-DD calendar day by deltaDays in UTC. */
export const shiftDay = (day: string, deltaDays: number): string => {
  const [y, m, d] = day.split("-").map((v) => Number(v));
  const date = new Date(Date.UTC(y, m - 1, d + deltaDays));
  return date.toISOString().substring(0, ISO_DATE_PREFIX_LENGTH);
};

/**
 * Carve Available windows inside [yearStart, yearEnd] around blocked ranges.
 * Blocked ends are inclusive. Missing `end` means Ongoing — no availability after that block.
 */
export const carveAvailableRangesAroundBlocks = (
  yearStart: string,
  yearEnd: string,
  blockedRanges: Array<{ start: string; end?: string }>,
): Array<{ start: string; end: string }> => {
  const sortedRanges = [...blockedRanges].sort((a, b) =>
    a.start.localeCompare(b.start),
  );

  const availableRanges: Array<{ start: string; end: string }> = [];
  let currentStart = yearStart;

  for (const blocked of sortedRanges) {
    const availableEnd = shiftDay(blocked.start, -1);

    if (currentStart <= availableEnd) {
      availableRanges.push({
        start: currentStart,
        end: availableEnd,
      });
    }

    // Ongoing block: do not resume availability after the start date
    if (!blocked.end) {
      return availableRanges;
    }

    const nextStart = shiftDay(blocked.end, 1);
    if (nextStart > currentStart) currentStart = nextStart;
  }

  if (currentStart <= yearEnd) {
    availableRanges.push({
      start: currentStart,
      end: yearEnd,
    });
  }

  return availableRanges;
};

export interface DateRange {
  start: string;
  end: string;
}

/** Merge overlapping/adjacent inclusive YYYY-MM-DD ranges into sorted, non-overlapping ranges. */
const mergeDateRanges = (ranges: DateRange[]): DateRange[] => {
  const sorted = [...ranges].sort((a, b) => a.start.localeCompare(b.start));
  const merged: DateRange[] = [];

  for (const range of sorted) {
    const last = merged[merged.length - 1];
    if (last && range.start <= shiftDay(last.end, 1)) {
      if (range.end > last.end) last.end = range.end;
    } else {
      merged.push({ ...range });
    }
  }

  return merged;
};

/**
 * Collect the Open (not `is_closed`) season windows from a harbor's `seasonal_dates`
 * config, clipped to [rangeStart, rangeEnd]. Closed seasons and gaps not covered by
 * any season entry are excluded - a seasonal berth is only available inside an
 * explicitly Open season.
 */
export const getOpenSeasonRanges = (
  seasonalDates: Record<string, SeasonDateRange[] | undefined> | undefined,
  rangeStart: string,
  rangeEnd: string,
): DateRange[] => {
  if (!seasonalDates) return [];

  const openRanges: DateRange[] = [];
  for (const seasonRanges of Object.values(seasonalDates)) {
    if (!Array.isArray(seasonRanges)) continue;

    for (const season of seasonRanges) {
      if (season.is_closed || !season.start_date || !season.end_date) continue;

      const start = season.start_date.substring(0, ISO_DATE_PREFIX_LENGTH);
      const end = season.end_date.substring(0, ISO_DATE_PREFIX_LENGTH);
      const clippedStart = start < rangeStart ? rangeStart : start;
      const clippedEnd = end > rangeEnd ? rangeEnd : end;

      if (clippedStart <= clippedEnd) {
        openRanges.push({ start: clippedStart, end: clippedEnd });
      }
    }
  }

  return mergeDateRanges(openRanges);
};

/** Intersect two lists of inclusive YYYY-MM-DD date ranges. */
export const intersectDateRanges = (
  rangesA: DateRange[],
  rangesB: DateRange[],
): DateRange[] => {
  const result: DateRange[] = [];

  for (const a of rangesA) {
    for (const b of rangesB) {
      const start = a.start > b.start ? a.start : b.start;
      const end = a.end < b.end ? a.end : b.end;
      if (start <= end) result.push({ start, end });
    }
  }

  return result.sort((x, y) => x.start.localeCompare(y.start));
};

export const getStatusCount = (events: CalendarEvent[], status: string) =>
  events.filter((e) => e.status === status).length;

export const getAssetTypeCount = (events: CalendarEvent[], assetType: string) =>
  events.filter((e) => e.assetType === assetType).length;

export const getBerthOptions = (berthsData: BerthDetails[] | undefined) => {
  if (!berthsData) return [];

  return berthsData.map((berth) => ({
    value: berth.berth_number,
    label: `Berth ${berth.berth_number}`,
  }));
};

export const getAddonOptions = (
  addonsData: Array<{ data?: IAddonData } | IAddonData> | undefined,
) => {
  if (!addonsData) return [];

  return addonsData.map((addonItem) => {
    const addon = extractAddonData(addonItem);
    return {
      value: addon.id,
      label: addon.name,
    };
  });
};

export const getAddonName = (
  addonId: string | null,
  addonsData: Array<{ data?: IAddonData } | IAddonData> | undefined,
): string => {
  if (!addonId || !addonsData) return "Add-on";

  const addonItem = addonsData.find((a) => {
    const addon = extractAddonData(a);
    return addon.id === addonId;
  });

  if (!addonItem) return "Add-on";

  const addon = extractAddonData(addonItem);
  return addon.name;
};
